import { useEffect } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Code, Heading1, Heading2, Italic, Link as LinkIcon, List,
  ListOrdered, Quote, Redo, Strikethrough, Undo,
} from "lucide-react";

import { cn } from "@/lib/utils";

interface RichEditorProps {
  value: string;                       // HTML string in / out — backend stores HTML.
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Minimum visible height for the editing area. */
  minHeightClass?: string;             // e.g. "min-h-[160px]"
}

/**
 * Tiptap-based HTML editor. Emits HTML strings so the existing
 * `<div dangerouslySetInnerHTML />` render paths on detail pages
 * keep working unchanged. Headless — styled with Tailwind + the
 * existing `prose` typography plugin.
 */
export function RichEditor({
  value, onChange, placeholder, disabled, className, minHeightClass,
}: RichEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Visual defaults — `prose` plugin styles these on render.
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noreferrer", target: "_blank" },
      }),
      Placeholder.configure({ placeholder: placeholder || "Write…" }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate: ({ editor }) => {
      // Tiptap returns "<p></p>" for empty docs — flatten that to ""
      // so the parent form's "required" check works naturally.
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none p-3 focus:outline-none",
          // Make the placeholder readable (StarterKit gives it
          // .is-editor-empty:first-child::before via Placeholder).
          "[&_p.is-editor-empty:first-child::before]:text-muted-foreground",
          "[&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
          "[&_p.is-editor-empty:first-child::before]:float-left",
          "[&_p.is-editor-empty:first-child::before]:h-0",
          "[&_p.is-editor-empty:first-child::before]:pointer-events-none",
          minHeightClass || "min-h-[160px]",
        ),
      },
    },
  });

  // Keep editor content in sync if the parent re-seeds `value`
  // (e.g. when a form opens in edit mode after the editor mounted).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    // Avoid the cursor jumping while typing — only set when truly different.
    if (incoming !== current && incoming !== (current === "<p></p>" ? "" : current)) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [editor, value]);

  // Keep `editable` state in sync with the `disabled` prop.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div className={cn(
        "rounded-md border border-input bg-background",
        minHeightClass || "min-h-[160px]",
        className,
      )} />
    );
  }

  return (
    <div className={cn(
      "rounded-md border border-input bg-background",
      "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
      className,
    )}>
      <Toolbar editor={editor} disabled={!!disabled} />
      <EditorContent editor={editor} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar — small set of marks + blocks, plus undo/redo. Enough for staff
// posts; not trying to replicate a full word-processor.
// ---------------------------------------------------------------------------
function Toolbar({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  function promptLink() {
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL", previous || "https://");
    if (url === null) return;          // cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border/60 bg-muted/30 p-1">
      <ToolbarButton
        label="Bold"
        active={editor.isActive("bold")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
        icon={Bold}
      />
      <ToolbarButton
        label="Italic"
        active={editor.isActive("italic")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        icon={Italic}
      />
      <ToolbarButton
        label="Strikethrough"
        active={editor.isActive("strike")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        icon={Strikethrough}
      />
      <ToolbarButton
        label="Inline code"
        active={editor.isActive("code")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleCode().run()}
        icon={Code}
      />

      <Divider />

      <ToolbarButton
        label="Heading 1"
        active={editor.isActive("heading", { level: 1 })}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        icon={Heading1}
      />
      <ToolbarButton
        label="Heading 2"
        active={editor.isActive("heading", { level: 2 })}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        icon={Heading2}
      />

      <Divider />

      <ToolbarButton
        label="Bulleted list"
        active={editor.isActive("bulletList")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        icon={List}
      />
      <ToolbarButton
        label="Numbered list"
        active={editor.isActive("orderedList")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        icon={ListOrdered}
      />
      <ToolbarButton
        label="Quote"
        active={editor.isActive("blockquote")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        icon={Quote}
      />

      <Divider />

      <ToolbarButton
        label="Link"
        active={editor.isActive("link")}
        disabled={disabled}
        onClick={promptLink}
        icon={LinkIcon}
      />

      <div className="ml-auto flex items-center gap-0.5">
        <ToolbarButton
          label="Undo"
          disabled={disabled || !editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
          icon={Undo}
        />
        <ToolbarButton
          label="Redo"
          disabled={disabled || !editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
          icon={Redo}
        />
      </div>
    </div>
  );
}

interface ToolbarButtonProps {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
}

function ToolbarButton({ label, active, disabled, onClick, icon: Icon }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded transition-colors",
        "hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:hover:bg-transparent",
        active && "bg-primary/10 text-primary",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function Divider() {
  return <div className="mx-1 h-5 w-px bg-border/60" aria-hidden />;
}
